import { Button, trackClick, trackEvent, useTranslation } from '@helios/frontend-core';
import { PalmettoIcon } from '@palmetto/dsm-components';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { SunnyIcon } from './SunnyIcon';
import { BAR_PREVIEW_TRANSITION_MS, COLLAPSED_BAR_MIN_WIDTH, EXPANDED_WIDTH } from './constants';
import { QuestionList } from './QuestionList';
import { UNREAD_INDICATOR_BOUNCE_CLASS, UNREAD_INDICATOR_BOUNCE_KEYFRAMES } from './unreadBounceAnimation';
import { useAutoGrowTextarea } from './useAutoGrowTextarea';
import { useBarPreviewCycle } from './useBarPreviewCycle';
import { useCollapseOnOutsideFocus } from './useCollapseOnOutsideFocus';
import { useElementSize } from './useElementSize';

export type SunnyChatMode = 'bar' | 'expanded' | 'launcher';

export interface SunnyChatProps {
  mode: SunnyChatMode;
  questions?: string[];
  hasUnread?: boolean;
  trackingId: string;
  onExpand: () => void;
  onCollapse: () => void;
  onSelectQuestion: (question: string) => void;
  onSubmit: (message: string) => void;
  onLauncherClick: () => void;
  className?: string;
  style?: React.CSSProperties;
  maxWidth?: number;
  fillBarWidth?: boolean;
  enableQuestionPreview?: boolean;
  /** Whether this is sitting inline in the bottom nav's own row (beside it), rather than above it. */
  isInlineWithNav?: boolean;
}

export const SunnyChat: React.FC<SunnyChatProps> = ({
  mode,
  questions = [],
  hasUnread = false,
  trackingId,
  onExpand,
  onCollapse,
  onSelectQuestion,
  onSubmit,
  onLauncherClick,
  className,
  style,
  maxWidth = EXPANDED_WIDTH,
  fillBarWidth = false,
  enableQuestionPreview = false,
  isInlineWithNav = false,
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const isExpanded = mode === 'expanded';
  const isLauncher = mode === 'launcher';
  const isClickableRow = !isExpanded;

  useEffect(() => {
    if (mode === 'expanded') {
      textareaRef.current?.focus({ preventScroll: true });
    }
  }, [mode]);

  const {
    slots: barPreviewSlots,
    activeIndex: previewIndex,
    isInstantJump: instantPreviewJump,
  } = useBarPreviewCycle(mode === 'bar', questions, t('sunnyChat.inputPlaceholder'), enableQuestionPreview);

  const barWidthMeasureRef = useRef<HTMLDivElement>(null);
  const { width: measuredBarWidth } = useElementSize(barWidthMeasureRef, [previewIndex, mode]);
  const barWidth = fillBarWidth
    ? maxWidth
    : measuredBarWidth
    ? Math.min(measuredBarWidth, maxWidth)
    : Math.min(COLLAPSED_BAR_MIN_WIDTH, maxWidth);

  useAutoGrowTextarea(textareaRef, message);

  const questionListContentRef = useRef<HTMLDivElement>(null);
  const { height: questionListHeight } = useElementSize(questionListContentRef);

  const handleRootBlur = useCollapseOnOutsideFocus(rootRef, onCollapse);

  const submitMessage = () => {
    const trimmed = message.trim();
    if (!trimmed) {
      return;
    }
    trackEvent('sunny chat message submitted', { source: 'typed', page: window.location.href });
    onSubmit(trimmed);
    setMessage('');
  };

  const selectQuestion = (question: string) => {
    trackEvent('sunny chat message submitted', { source: 'question', page: window.location.href });
    onSelectQuestion(question);
  };

  const expandFromBar = () => {
    trackClick(`${trackingId}-bar`, 'button', { page: window.location.href });
    onExpand();
  };

  const launcherClick = () => {
    trackClick(`${trackingId}-launcher`, 'button', { page: window.location.href });
    onLauncherClick();
  };

  const handleRowActivate = () => {
    if (mode === 'bar') {
      expandFromBar();
    } else if (isLauncher) {
      launcherClick();
    }
  };

  const widthTransition = 'transition-[width] duration-300 ease-out';
  const heightTransition = 'transition-[grid-template-rows] duration-300 ease-out';
  const cardTransitionStyle: React.CSSProperties = {
    transition: 'margin-top 300ms ease-out, background-color 300ms ease-out',
  };

  const rowAriaLabel = isLauncher
    ? hasUnread
      ? t('sunnyChat.unreadIndicatorLabel')
      : t('sunnyChat.launcherLabel')
    : undefined;

  return (
    <div
      ref={rootRef}
      onBlur={isExpanded ? handleRootBlur : undefined}
      style={{ width: isExpanded ? maxWidth : isLauncher ? undefined : barWidth, ...style }}
      className={classNames(
        'relative flex flex-col',
        widthTransition,
        {
          'w-[54px] h-[54px]': isLauncher,
          [UNREAD_INDICATOR_BOUNCE_CLASS]: isLauncher && hasUnread,
        },
        className,
      )}
    >
      <style>{UNREAD_INDICATOR_BOUNCE_KEYFRAMES}</style>
      {mode === 'bar' && (
        <div
          ref={barWidthMeasureRef}
          aria-hidden="true"
          className="invisible absolute left-0 top-0 flex items-center gap-8 whitespace-nowrap border-[3px] px-20 py-[14px]"
        >
          <div className="h-[20px] w-[20px]" />
          <span className="ui-400">{barPreviewSlots[previewIndex]}</span>
        </div>
      )}
      <QuestionList
        questions={questions}
        trackingId={trackingId}
        isExpanded={isExpanded}
        height={questionListHeight}
        contentRef={questionListContentRef}
        onSelectQuestion={selectQuestion}
      />
      <div
        style={cardTransitionStyle}
        className={classNames(
          'sunny-chat-card group relative flex flex-col overflow-hidden border-[3px] border-palmetto-300 transition-[border-radius] duration-300 ease-out',
          isExpanded ? '-mt-32 bg-grey-50' : 'mt-0 bg-palmetto-100',
          isLauncher ? 'rounded-full' : 'rounded-[32px]',
          {
            'shadow-[0px_6px_0px_-2px_theme(colors.palmetto-300)]': isInlineWithNav && isLauncher,
            'shadow-[0px_10px_0px_-6px_theme(colors.palmetto-300)]': isInlineWithNav && !isLauncher,
          },
        )}
      >
        <div
          role={isClickableRow ? 'button' : undefined}
          tabIndex={isClickableRow ? 0 : undefined}
          aria-label={rowAriaLabel}
          onClick={() => {
            if (isClickableRow) {
              handleRowActivate();
            }
          }}
          onKeyDown={(e) => {
            if (isClickableRow && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleRowActivate();
            }
          }}
          className={classNames(
            'flex items-center gap-8 transition-[padding] duration-300 ease-out',
            isLauncher ? 'justify-center p-12' : 'px-20 py-[14px]',
            { 'cursor-pointer': isClickableRow },
          )}
        >
          <SunnyIcon
            pixelSize={isLauncher ? 24 : 20}
            className="text-palmetto-500"
            style={{ transition: 'width 300ms ease-out, height 300ms ease-out' }}
            aria-hidden="true"
          />
          {!isLauncher && (
            <div
              className={classNames('relative min-w-0 flex-1 overflow-hidden', {
                'h-20 min-h-[20px]': !isExpanded,
              })}
            >
              {!isExpanded && questions.length > 0 && (
                <div
                  aria-hidden="true"
                  className="absolute inset-x-0 top-0 transition-transform ease-out"
                  style={{
                    transform: `translateY(calc(-100% / ${barPreviewSlots.length} * ${previewIndex}))`,
                    transitionDuration: instantPreviewJump ? '0ms' : `${BAR_PREVIEW_TRANSITION_MS}ms`,
                  }}
                >
                  {barPreviewSlots.map((slot, index) => (
                    <div
                      key={`${index}-${slot}`}
                      className="ui-400 h-20 min-h-[20px] whitespace-nowrap text-palmetto-700"
                    >
                      {slot}
                    </div>
                  ))}
                </div>
              )}
              <textarea
                ref={textareaRef}
                aria-label={t('sunnyChat.launcherLabel')}
                placeholder={t('sunnyChat.inputPlaceholder')}
                value={message}
                readOnly={!isExpanded}
                tabIndex={isExpanded ? undefined : -1}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submitMessage();
                  } else if (e.key === 'Escape') {
                    onCollapse();
                  }
                }}
                rows={1}
                className={classNames(
                  'ui-400 block max-h-[60px] w-full resize-none overflow-y-auto border-none bg-transparent text-palmetto-700 outline-none transition-[height] duration-150 ease-out placeholder:text-palmetto-700 placeholder:opacity-100',
                  isExpanded ? 'cursor-text' : 'cursor-pointer',
                  !isExpanded && questions.length > 0 ? 'opacity-0' : 'opacity-100',
                )}
              />
            </div>
          )}
        </div>
        <div
          aria-hidden={!isExpanded}
          className={classNames(
            'grid overflow-hidden',
            heightTransition,
            isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div className="overflow-hidden">
            <div className="flex justify-end p-16 pt-0" onClick={() => textareaRef.current?.focus()}>
              <Button
                trackingId={`${trackingId}-submit`}
                emphasis="primary"
                layout="hug"
                onClick={submitMessage}
                disabled={!message.trim()}
                aria-label={t('sunnyChat.submitLabel')}
                tabIndex={isExpanded ? 0 : -1}
                className="!rounded-10 !p-8 transition-opacity duration-200"
              >
                <PalmettoIcon name="arrowUp" size="xs" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isLauncher && hasUnread && (
        <span
          className="absolute -right-4 -top-4 h-24 w-24"
          role="status"
          aria-label={t('sunnyChat.unreadIndicatorLabel')}
        >
          <span className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-red-500" />
        </span>
      )}
    </div>
  );
};

export default SunnyChat;
